import BudgetIcon from "@assets/budget/budget-2026-logo.svg";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import Heading from "@elements/Typography/Heading";
import BHBasePage, { type BHBasePageProps } from "@pages/BHBasePage";
import ErrorPage from "@pages/ErrorPage";
import WidgetWrapper from "@pages/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@pages/NotFound";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import {
  CustomContext,
  ResponseType,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { BUGET_2026_PAGE } from "@src/app/config/budget";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";

import GEKeywordArticlesList from "./Components/GEKeywordArticleList";
import GELiveBlog from "./Components/GELiveBlog";
import { GEPayloadResponseType } from "./GeneralElections.server";
import GeneralElectionsMetaData from "./GeneralElectionsMetaData";

export function GeneralElections() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<GEPayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const adsSectionName = BUGET_2026_PAGE;
  const { gaData, metaTags } = GeneralElectionsMetaData(BUGET_2026_PAGE);
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: BUGET_2026_PAGE,
    gaData: gaData,
    metaTags: metaTags,
    pageAdTargetValue: pageAdTargetValue,
    hasLb1Ads: true,
    showGEBanner: false,
    bodyWrapperStyle: "bg-white-200 my-0 lg:my-0 py-3 lg:py-md",
  };

  const { tagsData, geRecordedVideo, geLiveBlog, budgetBooklet } =
    dataLoaderResponse.payload;

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <div>
          <div className="border-b-[1px] border-grey-400 pb-[10px] md:pb-[20px] mb-[10px] md:mb-[20px]">
            <div className="gap-xs hidden md:flex justify-between items-center">
              <Heading className="text-red-100 text-[30px] font-semibold font-secondary ">
                Budget 2026
              </Heading>
              <img src={BudgetIcon} alt="ge-icon" width={106} height={36} />
            </div>
            <div className="flex gap-xs md:hidden justify-between items-center">
              <Heading className="text-red-100 text-[18px] font-semibold font-secondary">
                Budget 2026
              </Heading>
              <img src={BudgetIcon} alt="ge-icon" width={74} height={25} />
            </div>
          </div>
        </div>
        <div className="flex gap-[40px]">
          <div className="w-full lg:w-[992px] xl:w-[847px] flex flex-col gap-5">
            <div dangerouslySetInnerHTML={{ __html: geRecordedVideo }} />
            {(geLiveBlog || budgetBooklet) && (
              <div className="xl:hidden mx-[10px] lg:mx-0">
                <div className="flex flex-col gap-5">
                  {budgetBooklet && (
                    <div dangerouslySetInnerHTML={{ __html: budgetBooklet }} />
                  )}
                  {geLiveBlog && (
                    <GELiveBlog variant="body" geLiveBlogContent={geLiveBlog} />
                  )}
                </div>
              </div>
            )}
            <GEKeywordArticlesList
              articles={tagsData}
              adsSectionName={adsSectionName}
            />
          </div>
          <div className="hidden xl:block">
            {geLiveBlog || budgetBooklet ? (
              <div className="flex flex-col gap-5 max-w-[300px]">
                {budgetBooklet && (
                  <div dangerouslySetInnerHTML={{ __html: budgetBooklet }} />
                )}
                {geLiveBlog && (
                  <GELiveBlog
                    variant="sidebar"
                    geLiveBlogContent={geLiveBlog}
                  />
                )}
              </div>
            ) : (
              <AdsImu2
                uniqueSectionName={adsSectionName}
                pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
              />
            )}
          </div>
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}
